import React, { useEffect, useState } from 'react';
import AppNav from './AppNav';
import { useRipple } from '../hooks/useRipple';
import AdUnit from './AdUnit';
import './styles/clickEffects.css';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

export default function AboutPage({
  priceMode = 'student', onModeToggle, onNavigate,
  user = null, onUserChange,
}) {
  const isMobile = useIsMobile();
  const navH = isMobile ? 60 : 72;

  const highlights = [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      ),
      title: 'Reliable Price Insights',
      desc: 'Data-backed predictions drawn from 24 years of historical WFP market records across Nigeria.',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      title: 'Real-Time Market Updates',
      desc: 'Live price monitoring and market intelligence refreshed continuously to keep you informed.',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      title: 'User-Focused Platform',
      desc: 'Designed for clarity and ease of use — from first-time buyers to experienced market participants.',
    },
  ];

  const missions = [
    { label: 'Price Transparency', desc: 'Making market pricing accessible and understandable to all.' },
    { label: 'Data-Driven Decisions', desc: 'Empowering users with reliable intelligence, not guesswork.' },
    { label: 'Reduced Market Uncertainty', desc: 'Helping users anticipate price movements before they happen.' },
  ];

  const techFeatures = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
      ),
      title: 'XGBoost Predictive Models',
      desc: 'Gradient-boosted machine learning models trained on decades of WFP price data across 30 commodities.',
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
        </svg>
      ),
      title: 'Scalable Data Processing',
      desc: 'Built to handle high-volume market data efficiently, with consistent output and minimal latency.',
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      ),
      title: 'Continuous Improvement',
      desc: 'Models are refined over time as more data becomes available, improving prediction quality.',
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      title: 'Fast Response System',
      desc: 'Predictions are delivered instantly with a structured, reliable output every time.',
    },
  ];

  return (
    <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeLeft { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        .about-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .about-card:hover { transform: translateY(-3px) !important; box-shadow: 10px 10px 0px 0px rgba(212,160,23,0.15) !important; }
      `}</style>

      <AppNav
        activePage="about"
        priceMode={priceMode}
        onModeToggle={onModeToggle}
        onNavigate={onNavigate}
        isMobile={isMobile}
        user={user}
        onUserChange={onUserChange}
      />
      <div style={{ height: navH }} />

      {/* ── Hero Banner — with farmers image background ── */}
      <div style={{
        position: 'relative', overflow: 'hidden',
        padding: isMobile ? '3rem 1.25rem 3.5rem' : '5rem 2rem 5.5rem',
      }}>
        {/* Background image */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(https://d1jcea4y7xhp7l.cloudfront.net/2022/01/Farmers-at-Work-in-their-Various-Farms.jpg)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          zIndex: 0,
        }} />
        {/* Dark green overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(10,46,20,0.92) 0%, rgba(26,71,49,0.88) 60%, rgba(13,59,26,0.93) 100%)',
          zIndex: 1,
        }} />
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -60, right: -60, width: 300, height: 300, borderRadius: '50%', background: 'rgba(212,160,23,0.05)', pointerEvents: 'none', zIndex: 2 }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none', zIndex: 2 }} />

        <div style={{ position: 'relative', zIndex: 3, maxWidth: 1280, margin: '0 auto', animation: 'fadeUp 0.6s ease both' }}>
          <h1 style={{
            fontFamily: 'Epilogue, sans-serif',
            fontSize: isMobile ? 'clamp(2rem, 9vw, 3rem)' : 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', lineHeight: 1.05,
            marginBottom: 20, maxWidth: 700,
          }}>
            Data-Driven Market<br />
            <span style={{ color: '#d4a017' }}>Intelligence</span> for Nigeria.
          </h1>
          <p style={{ fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, maxWidth: 580 }}>
            We provide a platform that helps users understand Nigerian market price trends and make informed decisions through data analytics and predictive modelling.
          </p>
        </div>
      </div>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '2rem 1rem 4rem' : '4rem 2rem 6rem' }}>

        {/* ── Key Highlights ── */}
        <section style={{ marginBottom: isMobile ? '3rem' : '5rem', animation: 'fadeUp 0.6s ease both 0.1s', animationFillMode: 'both' }}>
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ width: 4, height: 24, background: '#d4a017', display: 'inline-block', borderRadius: 2 }} />
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#727970', fontFamily: 'Space Grotesk, sans-serif' }}>Key Highlights</span>
            </div>
            <h2 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 22 : 28, fontWeight: 800, color: '#001805', letterSpacing: '-0.02em', margin: 0 }}>
              What We Offer
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
            {highlights.map((h, i) => (
              <div
                key={i}
                className="about-card"
                style={{
                  background: '#ffffff',
                  border: '1px solid rgba(10,46,20,0.1)',
                  boxShadow: '6px 6px 0px 0px rgba(10,46,20,0.06)',
                  padding: '1.75rem',
                  animationDelay: `${i * 80}ms`,
                }}
              >
                <div style={{
                  width: 48, height: 48, borderRadius: 10,
                  background: 'linear-gradient(135deg, #0a2e14 0%, #1a4731 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#d4a017', marginBottom: 16,
                  boxShadow: '0 4px 12px rgba(10,46,20,0.2)',
                }}>
                  {h.icon}
                </div>
                <h3 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: 16, fontWeight: 800, color: '#001805', marginBottom: 8, letterSpacing: '-0.01em' }}>
                  {h.title}
                </h3>
                <p style={{ fontSize: 13, color: '#424841', lineHeight: 1.65, margin: 0 }}>{h.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Our Mission ── */}
        <section style={{ marginBottom: isMobile ? '3rem' : '5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '2rem' : '4rem', alignItems: 'center' }}>
            {/* Left */}
            <div style={{ animation: 'fadeLeft 0.6s ease both 0.2s', animationFillMode: 'both' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ width: 4, height: 24, background: '#d4a017', display: 'inline-block', borderRadius: 2 }} />
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#727970', fontFamily: 'Space Grotesk, sans-serif' }}>Our Mission</span>
              </div>
              <h2 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 22 : 30, fontWeight: 900, color: '#001805', letterSpacing: '-0.02em', marginBottom: 16, lineHeight: 1.1 }}>
                Making Market Data<br />Accessible to All
              </h2>
              <p style={{ fontSize: 14, color: '#424841', lineHeight: 1.75, marginBottom: 24 }}>
                Our mission is to democratise access to Nigerian market intelligence — turning raw price data into clear, actionable insights that support better decisions for everyone.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {missions.map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{ width: 32, height: 32, background: '#d4a017', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderRadius: 6, fontSize: 12, fontWeight: 800, color: '#001805', fontFamily: 'Space Grotesk, sans-serif' }}>
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#001805', marginBottom: 3 }}>{m.label}</div>
                      <div style={{ fontSize: 13, color: '#424841', lineHeight: 1.6 }}>{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — vision callout WITH background image */}
            <div>
              <div style={{
                position: 'relative', overflow: 'hidden',
                padding: isMobile ? '2rem 1.5rem' : '2.5rem',
                boxShadow: '10px 10px 0px 0px rgba(212,160,23,0.15)',
                border: '1px solid rgba(212,160,23,0.2)',
              }}>
                {/* Background image */}
                <div style={{
                  position: 'absolute', inset: 0,
                  backgroundImage: 'url(https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSjh2Jb-jP448QS5ANNugxcYfSAqzrj3rt3lA&s)',
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  zIndex: 0,
                }} />
                {/* Dark green overlay */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(145deg, rgba(10,46,20,0.91) 0%, rgba(26,71,49,0.88) 100%)',
                  zIndex: 1,
                }} />

                {/* Content */}
                <div style={{ position: 'relative', zIndex: 2 }}>
                  <div style={{ width: 44, height: 44, background: 'rgba(212,160,23,0.15)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d4a017', marginBottom: 20 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 12 }}>
                    Our Vision
                  </div>
                  <h3 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 20 : 24, fontWeight: 900, color: '#ffffff', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 16 }}>
                    Nigeria's Most Trusted Source of Market Intelligence
                  </h3>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, marginBottom: 24 }}>
                    We envision a Nigeria where market data is no longer a privilege — where every user, regardless of background, can access the same quality of price intelligence to make confident decisions.
                  </p>
                  <div style={{ height: 1, background: 'rgba(212,160,23,0.25)', marginBottom: 20 }} />
                  <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                    {[{ val: '30+', label: 'Commodities' }, { val: '36+', label: 'States' }, { val: '24yrs', label: 'Data Range' }].map((s, i) => (
                      <div key={i}>
                        <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 22, fontWeight: 700, color: '#d4a017', lineHeight: 1 }}>{s.val}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── The Engine ── */}
        <section style={{ marginBottom: isMobile ? '3rem' : '5rem' }}>
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ width: 4, height: 24, background: '#d4a017', display: 'inline-block', borderRadius: 2 }} />
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#727970', fontFamily: 'Space Grotesk, sans-serif' }}>Technology</span>
            </div>
            <h2 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 22 : 28, fontWeight: 800, color: '#001805', letterSpacing: '-0.02em', marginBottom: 8, margin: 0 }}>
              The Engine Behind the Platform
            </h2>
            <p style={{ fontSize: 13, color: '#727970', marginTop: 8 }}>
              Advanced predictive analytics built on historical and real-time Nigerian market data.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 14 }}>
            {techFeatures.map((f, i) => (
              <div
                key={i}
                className="about-card"
                style={{
                  display: 'flex', gap: 16, alignItems: 'flex-start',
                  background: '#ffffff', padding: '1.25rem 1.5rem',
                  border: '1px solid rgba(10,46,20,0.08)',
                  boxShadow: '5px 5px 0px 0px rgba(10,46,20,0.05)',
                }}
              >
                <div style={{
                  width: 40, height: 40, flexShrink: 0, borderRadius: 8,
                  background: '#f6f3f2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#0a2e14',
                  border: '1px solid rgba(10,46,20,0.12)',
                }}>
                  {f.icon}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#001805', marginBottom: 5, fontFamily: 'Epilogue, sans-serif' }}>{f.title}</div>
                  <div style={{ fontSize: 12, color: '#424841', lineHeight: 1.6 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <AdUnit
          slot="9095850875"
          format="fluid"
          layoutKey="-fb+5w+4e-db+86"
          style={{ margin: '24px 0' }}
          requiresContent={false}
        />

        {/* ── Contact CTA — with golden hour farm image background ── */}
        <section>
          <div style={{
            position: 'relative', overflow: 'hidden',
            border: '1px solid rgba(212,160,23,0.2)',
            boxShadow: '10px 10px 0px 0px rgba(212,160,23,0.1)',
            display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between', gap: 24,
            padding: isMobile ? '2rem 1.5rem' : '3rem',
          }}>
            {/* Background image */}
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'url(https://static.vecteezy.com/system/resources/thumbnails/072/212/812/small/golden-hour-over-rows-of-lush-green-crops-in-agricultural-field-free-photo.jpg)',
              backgroundSize: 'cover', backgroundPosition: 'center',
              zIndex: 0,
            }} />
            {/* Dark overlay */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(135deg, rgba(10,46,20,0.93) 0%, rgba(26,71,49,0.88) 100%)',
              zIndex: 1,
            }} />

            {/* Content */}
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'Space Grotesk, sans-serif', marginBottom: 10 }}>
                Get in Touch
              </div>
              <h3 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: isMobile ? 20 : 26, fontWeight: 900, color: '#ffffff', letterSpacing: '-0.02em', marginBottom: 10, lineHeight: 1.15 }}>
                Have a question or want to learn more?
              </h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, maxWidth: 480 }}>
                Reach out to us for enquiries, partnerships, or support. Our team is available to help you get the most from NaijaAgro Predict.
              </p>
            </div>

            <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0 }}>
              <a
                href="mailto:naijaagropredict@gmail.com"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: '#d4a017', color: '#001805',
                  padding: '12px 22px', textDecoration: 'none',
                  fontSize: 13, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif',
                  boxShadow: '4px 4px 0px 0px rgba(0,0,0,0.2)',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '6px 6px 0px 0px rgba(0,0,0,0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '4px 4px 0px 0px rgba(0,0,0,0.2)'; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                naijaagropredict@gmail.com
              </a>
              <div style={{ display: 'flex', gap: 8 }}>
                {['08069305651', '07087917444'].map((num, i) => (
                  <a
                    key={i}
                    href={`tel:${num}`}
                    style={{
                      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                      background: 'rgba(255,255,255,0.1)', color: '#ffffff',
                      padding: '10px 16px', textDecoration: 'none',
                      fontSize: 12, fontWeight: 600, fontFamily: 'Space Grotesk, sans-serif',
                      border: '1px solid rgba(255,255,255,0.2)',
                      transition: 'background 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.13 6.13l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/></svg>
                    {num}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* ── Footer ── */}
      <footer style={{
        background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)',
        color: '#fff', padding: '1.75rem 2rem',
        borderTop: '3px solid #d4a017',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 12 }}>
          <div style={{ fontFamily: 'Epilogue, sans-serif', fontWeight: 800, fontSize: 15, color: '#d4a017' }}>NaijaAgro Predict</div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Home', nav: 'home' },
              { label: 'Terms', nav: 'terms' },
              { label: 'Privacy Policy', nav: 'privacy' },
              { label: 'Contact', nav: 'about' },
            ].map(l => (
              <button key={l.label} onClick={() => onNavigate && onNavigate(l.nav)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, color: 'rgba(255,255,255,0.6)',
                fontFamily: 'Manrope, sans-serif', padding: 0,
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#d4a017'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
              >{l.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>© 2026 NaijaAgro Predict. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}